<template>
  <kendo-grid
    v-bind="{ ...$attrs }"
    ref="grid"
    :columns="getColumns"
    :pageable="disablePaging ? null : pageable"
  >
    <template
      v-for="(_, slot) of $slots"
      #[slot]="scope"
    >
      <slot
        :name="slot"
        v-bind="scope"
      />
    </template>
    <!-- cell templates -->
    <template #simpleCell="{ props }">
      <td
        :data-th="getColumnTitle(props.field)"
        :class="props.className"
      >
        {{ getIndexPathValue(props.field, props.dataItem, isExcelImport, '-') }}
      </td>
    </template>
    <template #attributeCell="{ props }">
      <td
        :data-th="getColumnTitle(props.field)"
        :class="props.className"
      >
        {{ getTranslatedIndexPathValue(props.field, props.dataItem) }}
      </td>
    </template>
    <template #propCell="{ props }">
      <td
        :data-th="getColumnTitle(props.field)"
        :class="props.className"
      >
        {{ getTranslatedShortIndexPathValue(props.field, props.dataItem) }}
      </td>
    </template>
    <template #dateCell="{ props }">
      <td
        :data-th="getColumnTitle(props.field)"
        :class="props.className"
      >
        <FormatDate :date="getDate(props.field, props.dataItem)" />
      </td>
    </template>
    <template #dateTimeCell="{ props }">
      <td
        :data-th="getColumnTitle(props.field)"
        :class="props.className"
      >
        <FormatDate
          :date="getDate(props.field, props.dataItem)"
          :include-time="true"
          :include-seconds="false"
        />
      </td>
    </template>
    <template #emptyCell="{ props }">
      <td
        class="empty-column"
        :class="props.className"
      />
    </template>
    <!-- header templates -->
    <template #headerScrollLeft>
      <kbutton
        v-show="showScrollButtons"
        :theme-color="'primary'"
        @click="scrollGrid(true)"
        :disabled="scrollLeftDisabled"
      >
        <span class="k-icon k-i-arrow-chevron-left" />
      </kbutton>
    </template>
    <template #headerScrollRight>
      <kbutton
        v-show="showScrollButtons"
        :theme-color="'primary'"
        @click="scrollGrid(false)"
        :disabled="scrollRightDisabled"
      >
        <span class="k-icon k-i-arrow-chevron-right" />
      </kbutton>
    </template>
  </kendo-grid>
</template>

<script>
import {Grid} from '@progress/kendo-vue-grid'
import grid from '~/mixins/grid'
import throttle from 'lodash/throttle'
import {GRID_SETTINGS, MOBILE_BREAKPOINT} from '~/assets/js/constants'
import {Button} from '@progress/kendo-vue-buttons'
import {storeToRefs} from 'pinia'
import {useCartStore} from '@/store/cart'

export default defineComponent({
  name: 'Grid',
  mixins: [grid],
  inheritAttrs: false,
  components: {
    kbutton: Button,
    'kendo-grid': Grid
  },
  props: {
    tableColumns: {
      type: Array,
      required: false,
      default() {
        return []
      }
    },
    isExcelImport: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    },
    disablePaging: {
      type: Boolean,
      required: false,
      default() {
        return false
      }
    }
  },
  data() {
    return {
      kGridContentItem: null,
      kGridHeaderItem: null,
      unlockedColumnsSizes: null,
      lockedColumnsWidth: 0,
      scrollLeftDisabled: false,
      scrollRightDisabled: false,
      showScrollButtons: false,
      scrollLeftColumn: null,
      customScrollbarOuter: null,
      customScrollbarInner: null,
      gridTable: null,
      popupElement: null,
      ignoreScrolling: false,
      hasHierarchyCell: false,
      thrGridScrollEvents: null,
      thrWindowAndPopupScrollEvents: null,
      thrWindowResizeEvents: null,
      thrCustomScrollbarEvents: null,
      gridScrollingInitialized: false,
      customScrollbarInitialized: false,
      initializedGrid: false,
      pageable: {
        buttonCount: 3,
        info: false,
        type: 'numeric',
        pageSizes: [25],
        previousNext: true,
        pageSizeValue: false
      }
    }
  },
  beforeUnmount() {
    this.removeEventListeners()
  },
  setup() {
    const {addressDropDownVisible} = storeToRefs(useCartStore())
    const grid = ref(null)

    return {
      addressDropDownVisible,
      grid
    }
  },
  mounted() {
    this.initGridScrolling()
  },
  computed: {
    getColumns() {
      return this.extendTableColumnDefinitions(this.tableColumns)
    }
  },
  methods: {
    initGridScrolling() {
      const gridId = this.grid?._gridRoleElementId || undefined
      if (!gridId || !window || window.innerWidth < MOBILE_BREAKPOINT) {
        window.addEventListener('resize', this.onResize, {passive: true})
        return
      }
      const gridElement = document.getElementById(gridId)?.getElementsByClassName('k-grid-content')
      const gridHeaderElement = document.getElementById(gridId)?.getElementsByClassName('k-grid-header')
      if (gridElement && gridElement.length > 0 && gridHeaderElement && gridHeaderElement.length > 0) {
        this.kGridContentItem = gridElement[0]
        this.kGridHeaderItem = gridHeaderElement[0]
        if (this.initRowSettings()) {
          this.setScrollButtonStates()
          this.initCustomScrollbar()
          this.initEventListeners()
          this.gridScrollingInitialized = true
          this.initializedGrid = true
        }
      }
    },
    scrollGrid(reverse) {
      if (!this.gridScrollingInitialized || !this.kGridContentItem) {
        return
      }

      if (this.ignoreScrollingTimeout) {
        clearTimeout(this.ignoreScrollingTimeout)
      }

      let maxScrollLeft = this.kGridContentItem.scrollWidth - this.kGridContentItem.clientWidth
      let currentScrollLeft = this.kGridContentItem.scrollLeft
      let scrollAmount = 100

      if (!reverse) {
        currentScrollLeft += scrollAmount
      } else {
        currentScrollLeft -= scrollAmount
      }

      currentScrollLeft = Math.max(0, Math.min(currentScrollLeft, maxScrollLeft))
      this.ignoreScrolling = true
      this.kGridContentItem.scroll({left: currentScrollLeft, behavior: 'smooth'})
      const that = this

      this.ignoreScrollingTimeout = setTimeout(function () {
        that.ignoreScrolling = false
      }, 500)
    },
    extendTableColumnDefinitions(tableColumns) {
      const columns = [...tableColumns]
      let beforeUnlockedColumnIndex = null
      let afterUnlockedColumnIndex = null

      const that = this
      columns.forEach((column) => {
        if (this?.$attrs?.sortable !== undefined && column?.sortable !== false) {
          that.setColumnHeaderClass(column, 'sortable')
        }
      })

      // ignore if no locked column exists
      if (!columns.some(c => c.locked === true)) {
        return columns
      }

      columns.forEach((column, index) => {
        // ignore hidden columns
        if (column.width === '0') {
          return
        }

        that.setColumnClass(column, column.locked ? 'locked' : 'unlocked')
        if (beforeUnlockedColumnIndex === null && !column.locked) {
          beforeUnlockedColumnIndex = index
        }
        else if (beforeUnlockedColumnIndex !== null && column.locked && afterUnlockedColumnIndex === null) {
          afterUnlockedColumnIndex = columns.length > index ? index + 1 : index
        }

        return column
      })

      // add scroll left/right header cell
      if (beforeUnlockedColumnIndex !== null) {
        columns.splice(beforeUnlockedColumnIndex, 0, {className: 'locked before-unlocked', width: GRID_SETTINGS.SCROLL_COLUMN_WIDTH, locked: true, sortable: false, headerCell: 'headerScrollLeft', headerClassName: 'before-unlocked', editable: false})
      }
      if (afterUnlockedColumnIndex !== null) {
        columns.splice(afterUnlockedColumnIndex, 0, {className: 'locked after-unlocked', width: GRID_SETTINGS.SCROLL_COLUMN_WIDTH, locked: true, sortable: false, headerCell: 'headerScrollRight', headerClassName: 'after-unlocked', editable: false})
      }

      return columns
    },
    setColumnClass(column, className) {
      let classes = column.className ?? ''
      if (classes.indexOf(className) >= 0) {
        return
      }
      column.className = classes + ' ' + className
    },
    setColumnHeaderClass(column, className) {
      let classes = column.headerClassName ?? ''
      if (classes.indexOf(className) >= 0) {
        return
      }
      column.headerClassName = classes + ' ' + className
    },
    setScrollButtonStates() {
      this.showScrollButtons = this.lockedColumnsWidth <= this.kGridContentItem.clientWidth
      const gridScrollLeft = Math.ceil(this.kGridContentItem.scrollLeft)
      const maxScrollLeft = this.kGridContentItem.scrollWidth - this.kGridContentItem.clientWidth
      this.scrollLeftDisabled = gridScrollLeft === 0
      this.scrollRightDisabled = gridScrollLeft >= maxScrollLeft
    },
    initRowSettings() {
      const firstRow = this.kGridContentItem?.getElementsByClassName('k-master-row')[0] ?? null
      if (firstRow) {
        // unlocked columns
        const unlockedColumns = firstRow?.getElementsByClassName('unlocked')
        const unlockedColumnsSizes = []
        for (const unlockedColumn of unlockedColumns) {
          unlockedColumnsSizes.push(unlockedColumn.offsetWidth)
        }
        this.unlockedColumnsSizes = unlockedColumnsSizes

        // locked columns
        const lockedColumns = firstRow?.getElementsByClassName('locked')
        let lockedColumnsWidth = 0
        for (const column of lockedColumns) {
          lockedColumnsWidth += column.offsetWidth
        }
        this.lockedColumnsWidth = lockedColumnsWidth

        this.scrollLeftColumn = this.kGridHeaderItem?.getElementsByClassName('before-unlocked')[0] ?? null
        this.hasHierarchyCell = firstRow?.getElementsByClassName('k-hierarchy-cell').length > 0 ?? false

        return this.unlockedColumnsSizes.length > 0 && this.lockedColumnsWidth && this.scrollLeftColumn
      }
      return false
    },
    initCustomScrollbar() {
      const gridTable = this.kGridContentItem?.getElementsByClassName('k-grid-table')[0] ?? null
      if (!gridTable) {
        return
      }
      const outerScroll = document.createElement('div')
      outerScroll.className = this.addressDropDownVisible ? 'grid-scroll-outer custom-scrollbar hide deliveryAddressSpacer' : 'grid-scroll-outer custom-scrollbar hide'

      const innerScroll = document.createElement('div')
      innerScroll.className = 'grid-scroll-inner'

      outerScroll.appendChild(innerScroll)
      gridTable.parentNode?.insertBefore(outerScroll, gridTable)
      this.customScrollbarOuter = outerScroll
      this.customScrollbarInner = innerScroll
      this.gridTable = gridTable

      this.setCustomScrollbarPositionAndSize()
      this.setCustomScrollbarScrollPos()
      this.setCustomScrollbarVisibility()
      this.customScrollbarInitialized = true
    },
    setCustomScrollbarPositionAndSize() {
      // scrollbar width
      let lowScrollbarWidth = false
      let scrollbarWidth = this.kGridContentItem.clientWidth - this.lockedColumnsWidth - (this.hasHierarchyCell ? GRID_SETTINGS.HIERARCHY_CELL_WIDTH : 10)
      if ((lowScrollbarWidth = scrollbarWidth < GRID_SETTINGS.SCROLLBAR_THRESHOLD.MIN_WIDTH) === true) {
        scrollbarWidth = this.kGridContentItem.clientWidth - (GRID_SETTINGS.SCROLL_COLUMN_WIDTH)
      }
      if (this.customScrollbarOuter?.style) {
        this.customScrollbarOuter.style.width = scrollbarWidth + 'px'
      }
      if ( this.customScrollbarInner?.style) {
        this.customScrollbarInner.style.width = (parseInt(this.gridTable?.style?.width, 10) || this.gridTable.clientWidth) - this.kGridContentItem.clientWidth + scrollbarWidth + 'px'
      }
      // scrollbar position
      let scrollLeftColumn = this.scrollLeftColumn.getBoundingClientRect()
      if (this.customScrollbarOuter?.style) {
        this.customScrollbarOuter.style.left = lowScrollbarWidth ?
          this.kGridContentItem.getBoundingClientRect().left + (GRID_SETTINGS.SCROLL_COLUMN_WIDTH) + 'px' :
          scrollLeftColumn.right + 'px'
      }
    },
    setCustomScrollbarScrollPos() {
      this.customScrollbarOuter.scrollLeft = this.kGridContentItem.scrollLeft
    },
    setCustomScrollbarVisibility() {
      if (this.customScrollbarOuter) {
        const positions = this.kGridContentItem.getBoundingClientRect()
        if (this.scrollLeftDisabled && this.scrollRightDisabled) {
          this.customScrollbarOuter.classList.add('hide')
        }
        else if (positions.top + GRID_SETTINGS.SCROLLBAR_THRESHOLD.TOP < window.innerHeight && positions.bottom > window.innerHeight - GRID_SETTINGS.SCROLLBAR_THRESHOLD.BOTTOM) {
          this.customScrollbarOuter.classList.remove('hide')
          return
        }
        this.customScrollbarOuter.classList.add('hide')
      }
    },
    setDefaultScrollbarScrollPos() {
      if (!this.ignoreScrolling) {
        this.kGridContentItem.scrollLeft = this.customScrollbarOuter.scrollLeft
      }
    },
    windowResizeEvents() {
      this.setScrollButtonStates()
      if (this.customScrollbarInitialized) {
        this.setCustomScrollbarPositionAndSize()
        this.setCustomScrollbarVisibility()
      }
    },
    windowAndPopupScrollEvents() {
      this.setScrollButtonStates()
      if (this.customScrollbarInitialized) {
        this.setCustomScrollbarPositionAndSize()
        this.setCustomScrollbarVisibility()
      }
    },
    gridScrollEvents() {
      this.setScrollButtonStates()
      if (this.customScrollbarInitialized) {
        this.setCustomScrollbarScrollPos()
        this.setCustomScrollbarVisibility()
      }
    },
    customScrollbarEvents() {
      this.setDefaultScrollbarScrollPos()
    },
    initEventListeners() {
      this.thrGridScrollEvents = throttle(this.gridScrollEvents, 150)
      this.thrWindowResizeEvents = throttle(this.windowResizeEvents, 150)
      this.kGridContentItem.addEventListener('scroll', this.thrGridScrollEvents, {passive: true})
      window.addEventListener('resize', this.thrWindowResizeEvents, {passive: true})

      if (this.customScrollbarInitialized) {
        this.thrWindowAndPopupScrollEvents = throttle(this.windowAndPopupScrollEvents, 50)
        this.thrCustomScrollbarEvents = throttle(this.customScrollbarEvents, 50)

        const popup = this.kGridContentItem.closest('.popup')
        if (popup) {
          this.popupElement = popup
          popup.addEventListener('scroll', this.thrWindowAndPopupScrollEvents, {passive: true})
        } else {
          window.addEventListener('scroll', this.thrWindowAndPopupScrollEvents, {passive: true})
        }
        this.customScrollbarOuter.addEventListener('scroll', this.thrCustomScrollbarEvents, {passive: true})
      }
    },
    removeEventListeners() {
      if (this.thrGridScrollEvents !== null) {
        this.kGridContentItem?.removeEventListener('scroll', this.thrGridScrollEvents)
      }
      if (this.thrWindowResizeEvents !== null) {
        window?.removeEventListener('resize', this.thrWindowResizeEvents)
      }

      if (this.thrWindowAndPopupScrollEvents !== null) {
        if (this.popupElement) {
          this.popupElement?.removeEventListener('scroll', this.thrWindowAndPopupScrollEvents)
        } else {
          window?.removeEventListener('scroll', this.thrWindowAndPopupScrollEvents)
        }
      }

      if (this.thrCustomScrollbarEvents !== null) {
        this.customScrollbarOuter?.addEventListener('scroll', this.thrCustomScrollbarEvents)
      }
    },
    onResize() {
      if (typeof window !== 'undefined') {
        if (window.innerWidth > MOBILE_BREAKPOINT && !this.initializedGrid) {
          this.initGridScrolling()
          window.removeEventListener('resize', this.onResize)
        }
      }
    },
    adaptCustomScrollbar() {
      const scrollbar = this.kGridContentItem?.getElementsByClassName('grid-scroll-outer')[0] ?? null
      if (scrollbar) {
        !this.addressDropDownVisible ? scrollbar.classList.remove('deliveryAddressSpacer') : scrollbar.classList.add('deliveryAddressSpacer')
      }
    }
  },
  watch: {
    addressDropDownVisible: {
      handler() {
        this.adaptCustomScrollbar()
      }
    }
  }
})
</script>

<style lang="scss">
.deliveryAddressSpacer {
  bottom: 4rem !important;
}

.grid-scroll-outer {
  position: fixed;
  overflow: scroll hidden;
  bottom: .3rem;
  z-index: 2;
  box-shadow: .2rem .2rem .7rem .05rem $color--grid--scrollbar--shadow;
  background-color: $color__page--background;
  border-radius: .6rem;

  @include breakpoint-down($md) {
    display: none !important;
  }

  &.hide {
    opacity: 0;
    pointer-events: none;
  }

  &::-webkit-scrollbar {
    width: .6rem;
    height: .6rem;
  }

  .grid-scroll-inner {
    height: .0625rem;
  }
}

.k-pager-numbers-wrap {
  .k-button-flat-primary {
    .k-button-text{
      color: $color__primary;
      font-weight: $base-text-font-weight-semi-bold;
    }
  }

  .k-button-flat-primary.k-selected {
    .k-button-text{
      color: $color__white;
    }
  }
}

.k-pager-sizes {
  display: none;
}
</style>
